import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Sparkles, TrendingUp } from 'lucide-react';
import api from '../../lib/api';

// UPA brand accent per source. Instagram + Facebook get their vibe hue so the
// bar chart reads at a glance without a colour legend.
const SOURCE_STYLES = {
  Google:          { bar: 'from-sky-500 to-blue-600',      dot: 'bg-sky-400' },
  Instagram:       { bar: 'from-pink-500 to-fuchsia-600',  dot: 'bg-pink-400' },
  Facebook:        { bar: 'from-blue-600 to-indigo-700',   dot: 'bg-blue-400' },
  'Word of mouth': { bar: 'from-emerald-500 to-teal-600',  dot: 'bg-emerald-400' },
  Other:           { bar: 'from-amber-500 to-orange-600',  dot: 'bg-amber-400' },
  'Not specified': { bar: 'from-gray-600 to-gray-700',     dot: 'bg-gray-500' },
};

const PERIOD_OPTIONS = [
  { value: 30,  label: '30 days' },
  { value: 90,  label: '90 days' },
  { value: 365, label: '12 months' },
  { value: 0,   label: 'All time' },
];

const ReferralInsightsCard = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/free-trials/referral-insights?days=${days}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const rows = data?.by_source || [];
  const total = data?.total ?? 0;
  const withSource = data?.with_source ?? 0;
  const topLine = total === 0
    ? 'No free-trial submissions in this window yet.'
    : `${total} submission${total === 1 ? '' : 's'} · ${withSource} with a source`;

  return (
    <Card
      className="bg-gradient-to-br from-[#111] to-[#181818] border-[#2a2a2a]"
      data-testid="referral-insights-card"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
        <div className="min-w-0">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <Sparkles className="w-4 h-4 text-[#FF007F]" />
            How families are finding us
          </CardTitle>
          <p className="text-xs sm:text-sm text-white/50 mt-1" data-testid="referral-insights-topline">
            {loading ? 'Loading…' : topLine}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 shrink-0">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDays(opt.value)}
              className={
                'text-xs px-2.5 py-1 rounded-full border transition-colors ' +
                (days === opt.value
                  ? 'bg-[#FF007F] border-[#FF007F] text-white'
                  : 'border-[#333] text-white/70 hover:bg-[#1f1f1f]')
              }
              data-testid={`referral-insights-period-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {!loading && rows.length === 0 && (
          <div className="text-sm text-white/40 py-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Nothing to plot yet — new trial submissions will populate this widget automatically.
          </div>
        )}
        {rows.length > 0 && (
          <ul className="space-y-2.5" data-testid="referral-insights-list">
            {rows.map((row) => {
              const style = SOURCE_STYLES[row.source] || SOURCE_STYLES.Other;
              const barWidth = Math.max(row.pct, row.count > 0 ? 3 : 0); // guarantee sliver for 1+ count
              return (
                <li
                  key={row.source}
                  className="flex items-center gap-3"
                  data-testid={`referral-insights-row-${row.source.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-28 sm:w-32 shrink-0 flex items-center gap-2">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} />
                    <span className="text-sm text-white/80 truncate">{row.source}</span>
                  </div>
                  <div className="flex-1 h-4 bg-[#222] rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${style.bar} transition-all duration-500`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <div className="w-24 sm:w-28 shrink-0 text-right text-sm tabular-nums">
                    <span className="text-white font-medium">{row.count}</span>
                    <span className="text-white/40 ml-1.5">({row.pct}%)</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralInsightsCard;
